<!--
  Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
  This information is confidential and proprietary to GM Global Technology
  Operations LLC and may not be used, modified, copied or distributed.
  -->
@if (permission()) {
  <div class="cr-wrap">
    <div class="cr-eyebrow">IT Admin Tools</div>
    <h1 class="cr-title">Change Roles</h1>
    <p class="cr-lede">
      Temporarily take on a different set of roles to see eSCI the way another user would.
      Your real roles come back automatically when the timer runs out &mdash; or clear them yourself at any time.
    </p>

    @if (loading()) {
      <div class="cr-loading">Loading role options&hellip;</div>
    } @else if (accessDenied()) {
      <div class="cr-denied">
        <div class="cr-denied-ic" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" stroke="currentColor" stroke-width="1.7"/>
            <path d="M8 10.5V8a4 4 0 018 0v2.5" stroke="currentColor" stroke-width="1.7"/>
          </svg>
        </div>
        <h2>Role changing isn&rsquo;t available</h2>
        <p>This tool requires the <b>eSCI&nbsp;IT&nbsp;Admin</b> role. Ask an administrator if you need access.</p>
        <p class="cr-denied-recovery">
          If you applied temporary roles that removed IT Admin access, you can still clear them to restore your real roles.
        </p>
        <div class="cr-actions cr-denied-actions">
          <button type="button" class="cr-btn cr-btn-danger" [disabled]="saving()" (click)="clearRoles()">
            Clear temporary roles
          </button>
        </div>
      </div>
    } @else if (options(); as o) {
      @if (!o.enabled) {
        <!-- Feature flag off in this environment -->
        <div class="cr-denied">
          <div class="cr-denied-ic" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" stroke="currentColor" stroke-width="1.7"/>
              <path d="M8 10.5V8a4 4 0 018 0v2.5" stroke="currentColor" stroke-width="1.7"/>
            </svg>
          </div>
          <h2>Role changing isn&rsquo;t available</h2>
          <p>This tool is turned off in this environment (the <code>allowRoleModification</code> setting is disabled).</p>
        </div>
      } @else {
        <!-- Interactive page -->
        <div class="cr-grid-top">
          <div class="cr-card">
            <div class="cr-card-label">Session</div>
            <div class="cr-session-name">{{ o.userName }}</div>
            <div class="cr-kv"><span class="cr-k">GMID</span><span class="cr-v mono">{{ o.userGmid }}</span></div>

            <div class="cr-status">
              <span class="cr-dot calm"></span>
              <div>
                <div class="cr-stitle">Your real roles</div>
                <div class="cr-ssub">{{ o.realRoles.length }} role(s) from Graph</div>
              </div>
            </div>
          </div>

          <div class="cr-card">
            <div class="cr-card-label">Timeout</div>
            <div class="cr-timeout-row">
              <div class="cr-stepper">
                <button type="button" (click)="step(-1)" aria-label="Decrease timeout">&ndash;</button>
                <input class="mono" type="number" [min]="MIN_TIMEOUT" [max]="MAX_TIMEOUT"
                       [value]="timeoutMinutes()"
                       (change)="onTimeoutChange($any($event.target).value)"
                       aria-label="Timeout in minutes"/>
                <button type="button" (click)="step(1)" aria-label="Increase timeout">+</button>
              </div>
              <span class="cr-unit">minutes</span>
            </div>
            <div class="cr-hint">Between <b>{{ MIN_TIMEOUT }}</b> and <b>{{ MAX_TIMEOUT }}</b> (24&nbsp;h).
              Roles revert automatically when it expires.</div>
          </div>
        </div>

        <div class="cr-banner" role="note">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 3l9 16H3l9-16z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
            <path d="M12 10v4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
            <circle cx="12" cy="16.6" r="1" fill="currentColor"/>
          </svg>
          <div>Temporary roles <b>override your real MS&nbsp;Graph roles</b> across all of eSCI until they expire
            or you clear them. Only you are affected.</div>
        </div>

        <section class="cr-group">
          <div class="cr-group-head">
            <h2>Admin roles</h2><span class="cr-rule"></span>
            <span class="cr-count">{{ countSelected(o.adminRoles) }} selected</span>
          </div>
          <div class="cr-chips">
            @for (role of o.adminRoles; track role) {
              <button type="button" class="cr-chip"
                      [class.sel]="isSelected(role)" [class.current]="isReal(role)"
                      [attr.aria-pressed]="isSelected(role)" (click)="toggle(role)">
                <span class="cr-box" aria-hidden="true">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6.2l2.2 2.3 4.8-5" stroke="#fff" stroke-width="1.8"
                          stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <span>{{ role }}</span>
                @if (isReal(role)) { <span class="cr-current-tag">Real Role</span> }
              </button>
            }
          </div>
        </section>

        <section class="cr-group">
          <div class="cr-group-head">
            <h2>Specialist roles</h2><span class="cr-rule"></span>
            <span class="cr-count">{{ countSelected(o.specialistRoles) }} selected</span>
          </div>
          <div class="cr-chips">
            @for (role of o.specialistRoles; track role) {
              <button type="button" class="cr-chip"
                      [class.sel]="isSelected(role)" [class.current]="isReal(role)"
                      [attr.aria-pressed]="isSelected(role)" (click)="toggle(role)">
                <span class="cr-box" aria-hidden="true">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6.2l2.2 2.3 4.8-5" stroke="#fff" stroke-width="1.8"
                          stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <span>{{ role }}</span>
                @if (isReal(role)) { <span class="cr-current-tag">Real Role</span> }
              </button>
            }
          </div>
        </section>

        <section class="cr-group">
          <div class="cr-group-head">
            <h2>Other roles</h2><span class="cr-rule"></span>
            <span class="cr-count">{{ countSelected(o.otherRoles) }} selected</span>
          </div>
          <div class="cr-chips">
            @for (role of o.otherRoles; track role) {
              <button type="button" class="cr-chip"
                      [class.sel]="isSelected(role)" [class.current]="isReal(role)"
                      [attr.aria-pressed]="isSelected(role)" (click)="toggle(role)">
                <span class="cr-box" aria-hidden="true">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6.2l2.2 2.3 4.8-5" stroke="#fff" stroke-width="1.8"
                          stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <span>{{ role }}</span>
                @if (isReal(role)) { <span class="cr-current-tag">Real Role</span> }
              </button>
            }
          </div>
        </section>

        <div class="cr-legend">
          <span class="cr-li"><span class="cr-sw sel"></span> Selected (will be applied)</span>
          <span class="cr-li"><span class="cr-sw current"></span> Your real role (from Graph)</span>
          <span class="cr-li"><span class="cr-sw"></span> Available</span>
        </div>

        <div class="cr-actions">
          <button class="cr-btn cr-btn-primary" [disabled]="saving() || !dirty()" (click)="save()">Save roles</button>
          <button class="cr-btn cr-btn-danger" [disabled]="saving()" (click)="clearRoles()">
            Clear temporary roles
          </button>
          @if (dirty()) { <span class="cr-pill">Unsaved changes</span> }
          <button class="cr-btn cr-btn-plain" [disabled]="saving()" (click)="refresh()">Refresh</button>
        </div>
      }
    }
  </div>
}
